<script setup>
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import StudioDataSheet from './StudioDataSheet.vue'
import { stopChartEdit } from './charts/chartEditing.js'
import { applyCellInputs, cellAt, refAt, sheetColumns, sheetRows } from './objects/objectEdit.js'

// RED-317 PR 3d-B — an embedded Excel object's cells edited in place on the
// Studio canvas, like a chart's data (StudioChartEditor). It mounts into the
// object's host (the RawRun node view's controller): the data sheet slides
// open below the object's line, a click on the preview picks that cell, and
// typing redraws the preview live with the rules the compiler writes the
// new EMF by (objects/objectPreview.js). Every commit is one setObjectEdit
// transaction (one undo step, autosaved as any other change). What does not
// parse or fit stays in its cell, flagged, until fixed: never cleared, never
// applied, and "Valmis" waits for it. Rows cannot be added or removed here:
// Word draws the object at a fixed size; the toolbar says so when asked.
const props = defineProps({
  editor: { type: Object, required: true },
  controller: { type: Object, required: true },
})

const { t } = useI18n()

const nodeState = shallowRef(props.controller.state.value)
const unsubscribe = props.controller.state.subscribe((value) => {
  nodeState.value = value
})

const model = props.controller.model()
const fonts = props.controller.fonts()
const edit = computed(() => nodeState.value.node.attrs.objectEdit ?? null)

const messages = computed(() => ({
  number: t('messages.editor.object.invalidNumber'),
  fit: t('messages.editor.object.invalidFit'),
  char: t('messages.editor.object.invalidChar'),
  text: t('messages.editor.object.invalidText'),
  long: t('messages.editor.object.invalidLong'),
  merged: t('messages.editor.object.merged'),
  formula: t('messages.editor.object.formula'),
  readonly: t('messages.editor.object.readonly'),
}))
const columns = sheetColumns(model)
const invalid = ref({})
const rows = computed(() => sheetRows(model, edit.value, invalid.value, messages.value))
const invalidCount = computed(() => Object.keys(invalid.value).length)

const sheet = useTemplateRef('sheet')
const active = ref({ row: 0, col: 0 })
const refusal = ref(false)
const rowsNote = ref(false)

/**
 * The line under the sheet: after "Valmis" was refused, what to fix; else
 * why the active cell (or the cell typed last) was flagged, so the reason
 * is in view and not only in the cell's tooltip.
 */
const status = computed(() => {
  if (refusal.value && invalidCount.value) return t('messages.editor.object.fixInvalid', { count: invalidCount.value })
  const activeRef = refAt(model, active.value.row, active.value.col)
  const ref = activeRef && invalid.value[activeRef] ? activeRef : Object.keys(invalid.value).at(-1)
  return ref ? `${t('messages.editor.object.cellLabel', { ref })}: ${messages.value[invalid.value[ref].reason] ?? ''}` : ''
})

// ------------------------------------------------------------------ commits

function commit(next) {
  const pos = props.controller.pos()
  if (typeof pos !== 'number' || JSON.stringify(next ?? null) === JSON.stringify(edit.value)) return
  props.editor.commands.setObjectEdit(pos, next)
}

const withRefs = (inputs) => inputs.map(({ row, col, text }) => ({ ref: refAt(model, row, col), text })).filter((input) => input.ref)

function onDraft(draft) {
  if (!draft) {
    props.controller.preview(undefined)
    return
  }
  const result = applyCellInputs(model, fonts, edit.value, withRefs([draft]), {})
  props.controller.preview(Object.keys(result.invalid).length ? undefined : result.edit)
}

function onInput(inputs) {
  const result = applyCellInputs(model, fonts, edit.value, withRefs(inputs), invalid.value)
  invalid.value = result.invalid
  if (!invalidCount.value) refusal.value = false
  if (result.changed) commit(result.edit)
}

const undo = () => props.editor.commands.undo()
const redo = () => props.editor.commands.redo()

// ----------------------------------------------------------------- the exit

const expanded = ref(false)
let closing = false

/** "Valmis" or a click outside: collapse, unless a typed text is still invalid. */
function done() {
  if (closing) return
  if (invalidCount.value) {
    refusal.value = true
    const [ref] = Object.keys(invalid.value)
    const cell = model.cells[ref]
    if (cell) sheet.value?.focusCell(cell.row, cell.col)
    return
  }
  closing = true
  expanded.value = false
  const finish = () => stopChartEdit(props.editor)
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) finish()
  else window.setTimeout(finish, 200)
}

function onPanelKeydown(event) {
  if (event.key !== 'Escape' || event.defaultPrevented) return
  event.preventDefault()
  event.stopPropagation()
  done()
}

function onOutsidePointer(event) {
  const block = props.controller.block()
  const target = event.target
  if (!(target instanceof Node) || block.contains(target)) return
  if (target instanceof Element && target.closest('.p-tooltip')) return
  done()
}

// ------------------------------------------------- the preview's cells

const mark = shallowRef(null)

/** The frame's pixels per preview pixel, and its box relative to the host. */
function frameGeometry() {
  const frame = props.controller.frame()
  const host = props.controller.host()
  const svg = frame?.querySelector('svg')
  if (!frame || !host || !svg) return null
  const rect = frame.getBoundingClientRect()
  const origin = host.getBoundingClientRect()
  const viewBox = svg.viewBox.baseVal
  if (!viewBox?.width || !rect.width) return null
  return { rect, origin, scale: rect.width / viewBox.width, viewBox }
}

/** The outline over the active cell on the preview. */
function measure() {
  const geometry = frameGeometry()
  const ref = refAt(model, active.value.row, active.value.col)
  const cell = ref ? model.cells[model.cells[ref]?.covered ?? ref] : null
  if (!geometry || !cell) {
    mark.value = null
    return
  }
  const [x0, y0, x1, y1] = cell.box
  const { rect, origin, scale, viewBox } = geometry
  mark.value = {
    left: `${rect.left - origin.left + (x0 - viewBox.x) * scale}px`,
    top: `${rect.top - origin.top + (y0 - viewBox.y) * scale}px`,
    width: `${(x1 - x0 + 1) * scale}px`,
    height: `${(y1 - y0 + 1) * scale}px`,
  }
}

/** A press on the preview picks the cell under it; the keyboard stays in the sheet. */
function onPreviewPointer(event) {
  const host = props.controller.host()
  if (!(event.target instanceof Node) || host?.contains(event.target)) return
  event.preventDefault()
  const geometry = frameGeometry()
  if (geometry) {
    const x = geometry.viewBox.x + (event.clientX - geometry.rect.left) / geometry.scale
    const y = geometry.viewBox.y + (event.clientY - geometry.rect.top) / geometry.scale
    const cell = cellAt(model, x, y)
    if (cell) {
      sheet.value?.focusCell(cell.row, cell.col)
      return
    }
  }
  sheet.value?.focus()
}

function onSelect(cell) {
  active.value = cell
  measure()
}

function refuseRows() {
  rowsNote.value = true
}

// ------------------------------------------------------------ lifecycle

let offRender = null
let resize = null
onMounted(() => {
  offRender = props.controller.onRender(measure)
  resize = new ResizeObserver(measure)
  resize.observe(props.controller.host())
  props.controller.block().addEventListener('mousedown', onPreviewPointer)
  window.document.addEventListener('pointerdown', onOutsidePointer, true)
  requestAnimationFrame(() => {
    expanded.value = true
    const first = rows.value.findIndex((row) => row.cells.some((cell) => !cell.readonly))
    const col = first >= 0 ? rows.value[first].cells.findIndex((cell) => !cell.readonly) : 0
    sheet.value?.focusCell(Math.max(first, 0), Math.max(col, 0))
    measure()
  })
})

onBeforeUnmount(() => {
  unsubscribe()
  offRender?.()
  resize?.disconnect()
  props.controller.block().removeEventListener('mousedown', onPreviewPointer)
  window.document.removeEventListener('pointerdown', onOutsidePointer, true)
  props.controller.preview(undefined)
})

const cellLabel = (row, col) => {
  const ref = refAt(model, row, col)
  const cell = rows.value[row]?.cells[col]
  return [t('messages.editor.object.cellLabel', { ref: ref ?? '' }), cell?.display ?? ''].filter(Boolean).join(': ')
}

const toolClass = 'flex h-7 cursor-pointer items-center gap-1.5 rounded-md border-none bg-transparent px-2 text-[13px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
</script>

<template>
  <Teleport :to="controller.host()">
    <div v-if="mark" class="studio-object-cell-mark" :style="mark" aria-hidden="true" data-testid="studio-object-cell-mark" />
    <div class="studio-chart-edit-panel" :class="{ 'is-open': expanded }" data-testid="studio-object-editor" @keydown="onPanelKeydown">
      <div class="overflow-hidden">
        <div class="flex flex-col gap-2 pt-3 pb-1 font-sans">
          <div class="flex flex-wrap items-center gap-0.5" role="toolbar" :aria-label="t('messages.editor.object.toolbar')">
            <button type="button" :class="toolClass" aria-disabled="true" data-testid="studio-object-add-row" @click="refuseRows">
              <span class="pi pi-plus text-[11px]" aria-hidden="true" />{{ t('messages.editor.object.addRow') }}
            </button>
            <button type="button" :class="toolClass" aria-disabled="true" data-testid="studio-object-remove-row" @click="refuseRows">
              <span class="pi pi-minus text-[11px]" aria-hidden="true" />{{ t('messages.editor.object.removeRow') }}
            </button>
            <span class="ml-2 hidden text-[13px] text-gray-500 sm:inline">{{ t('messages.editor.object.hint') }}</span>
            <Button type="button" size="small" class="ml-auto" :label="t('messages.editor.object.done')" data-testid="studio-object-done" @click="done" />
          </div>
          <div v-if="rowsNote" class="flex items-start gap-1.5 text-[13px] text-gray-600" role="status" data-testid="studio-object-rows-note">
            <span class="pi pi-info-circle mt-0.5 text-[12px]" aria-hidden="true" />{{ t('messages.editor.object.rowsFixed') }}
          </div>
          <StudioDataSheet
            ref="sheet"
            :columns="columns"
            :rows="rows"
            :label="t('messages.editor.object.sheet')"
            :cell-label="cellLabel"
            :visible-rows="12"
            @draft="onDraft"
            @input="onInput"
            @select="onSelect"
            @undo="undo"
            @redo="redo"
          />
          <div class="min-h-5 text-[13px]" :class="status ? 'text-red-700' : 'invisible'" role="status" data-testid="studio-object-invalid">
            {{ status }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
